import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Autocomplete, TextField } from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";

import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { autocompleteDropdownStyles } from "Constants/Style";
import { City, Country, State } from "country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import { fetchALLUserFormDropdowns } from "slices/users/thunk";
export interface BasicDetailsProps {
  setActiveTab: (tabName: string) => void;
  isEdit?: boolean;
  user_id?: string;
}

const BasicDetails = ({
  setActiveTab,
  isEdit = false,
  user_id = "",
}: BasicDetailsProps) => {
  const local_data = JSON.parse(localStorage.getItem("authUser") || "{}") || {};

  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);

  // Initial form values
  const [initialValues, setInitialValues] = useState({
    organization: "",
    department: "",
    team: "",
    first_name: "",
    last_name: "",
    username: "",
    designation: "",
    user_type: "",
    email: "",
    password: "",
    password2: "",
    mobile_number: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
  });

  const commonInputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };
  const commonLabelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };
  const containerStyle = {
    marginTop: "2rem",
    marginLeft: 0,
    marginRight: 0,
    padding: "2rem 1rem",
    //maxWidth: 1200,
    width: "100%",
    borderRadius: "0.75rem",
  };
  const cardStyle = { padding: "2rem 1rem", borderRadius: "0.75rem" };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [teamOptions, setTeamOptions] = useState<any[]>([]);
  const [nextPage, setNextPage] = useState(false);

  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const isFormDataInitialized = useRef(false);
  const lastFetchedUserId = useRef<string>("");

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const {
    userTypes,
    organizations,
    departments: allDepartments,
    teams,
  } = useSelector((state: any) => state.users.userFormDropDowns);

  // Yup validation schema
  const validationSchema = Yup.object({
    organization: Yup.string().required("Organisation is required"),
    department: Yup.string().required("Department is required"),
    team: Yup.string().required("Team is required"),
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    username: Yup.string().required("Username is required"),
    designation: Yup.string().required("Designation is required"),
    user_type: Yup.string().required("User type is required"),
    email: Yup.string().email("Email is invalid").required("Email is required"),
    password: !user_id
      ? Yup.string()
          .min(8, "Password must be at least 8 characters")
          .required("Password is required")
      : Yup.string(),
    password2: !user_id
      ? Yup.string()
          .oneOf([Yup.ref("password")], "Passwords do not match")
          .required("Confirm password is required")
      : Yup.string(),
    mobile_number: Yup.string()
      .required("Mobile number is required")
      .matches(/^\d{10}$/, "Mobile No. must be 10 digits"),
    address: Yup.string().optional(),
    country: Yup.string().optional(),
    state: Yup.string().optional(),
    city: Yup.string().optional(),
    pincode: Yup.string()
      .optional()
      .test("pincode-format", "Pincode must be 6 digits", function (value) {
        if (!value || value === "") return true; // Optional, so empty is valid
        return /^\d{6}$/.test(value);
      }),
  });

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        localStorage.removeItem("permission_user");
        let response;

        console.log("Form data being sent:", values);
        console.log("User ID:", user_id);

        if (user_id) {
          // Prepare update payload - ensure IDs are sent correctly
          const updatePayload = {
            first_name: values.first_name,
            last_name: values.last_name,
            username: values.username,
            email: values.email,
            mobile_number: values.mobile_number,
            address: values.address,
            state: values.state,
            city: values.city,
            country: values.country,
            pincode: (values.pincode || "").toString(),
            designation: values.designation,
            organization: values.organization, // UUID string
            department: values.department, // UUID string
            team: values.team, // UUID string
            user_type: values.user_type, // UUID string
            updated_by: local_data?.user_id || local_data?.username,
          };
          console.log("Updating user with data:", updatePayload);
          response = await Axios.put(`/users/edit/${user_id}/`, updatePayload);
          console.log("Update response:", response);
        } else {
          // Prepare create payload - backend expects organization_id, department_id, etc.
          const createPayload = {
            first_name: values.first_name,
            last_name: values.last_name,
            username: values.username,
            email: values.email,
            password: values.password,
            password2: values.password2,
            mobile_number: values.mobile_number,
            address: values.address,
            state: values.state,
            city: values.city,
            country: values.country,
            pincode: (values.pincode || "").toString(),
            designation: values.designation,
            organization_id: values.organization, // UUID string
            department_id: values.department, // UUID string
            team_id: values.team, // UUID string
            user_type_id: values.user_type, // UUID string
          };
          console.log("Creating user with data:", createPayload);
          response = await Axios.post("/users/register/", createPayload);
        }

        if (response.status === 200 || response.status === 201) {
          console.log("Success response data:", response.data);
          toast.success(`User ${user_id ? "updated" : "created"} successfully`);

          if (user_id) {
            // For updates, refresh the form data with the updated user data
            const updatedUserData = response.data.data;
            if (updatedUserData) {
              // Extract IDs from nested objects
              const organizationId =
                updatedUserData.organization?.organization_id ||
                updatedUserData.organization ||
                "";
              const departmentId =
                updatedUserData.department?.department_id ||
                updatedUserData.department ||
                "";
              const teamId =
                updatedUserData.team?.team_id || updatedUserData.team || "";
              const userTypeId =
                updatedUserData.user_type?.user_type_id ||
                updatedUserData.user_type ||
                "";

              setInitialValues({
                organization: organizationId,
                department: departmentId,
                team: teamId,
                first_name: updatedUserData.first_name || "",
                last_name: updatedUserData.last_name || "",
                username: updatedUserData.username || "",
                designation: updatedUserData.designation || "",
                user_type: userTypeId,
                email: updatedUserData.email || "",
                password: "",
                password2: "",
                mobile_number: updatedUserData.mobile_number || "",
                address: updatedUserData.address || "",
                city: updatedUserData.city || "",
                state: updatedUserData.state || "",
                country: updatedUserData.country || "",
                pincode: updatedUserData.pincode || "",
              });

              // Update team options if department changed
              if (departmentId && teams && teams.length > 0) {
                const filteredTeams = teams.filter(
                  (team: any) => team.department_id === departmentId
                );
                setTeamOptions(filteredTeams);
              }

              // Refresh dropdown data to ensure consistency
              dispatch(fetchALLUserFormDropdowns());
            }
            // Stay on the same tab when editing (just refresh)
          } else {
            // For new users, proceed to permissions tab
            setTimeout(() => {
              localStorage.setItem(
                "permission_user",
                JSON.stringify(response?.data?.data?.user_id)
              );
              setNextPage(true);
              setActiveTab("permissions");
            }, 1000);
          }
        } else {
          console.log("Error response:", response);
          toast.error(`Failed to ${user_id ? "update" : "create"} user`);
        }
      } catch (error: any) {
        const errorData = error?.response?.data;

        console.log("errorData:", errorData);

        // Case 1: Check if error is a simple string message
        if (typeof errorData?.error === "string") {
          // Check if it looks like JSON (starts with { or [)
          const trimmedError = errorData.error.trim();
          if (trimmedError.startsWith("{") || trimmedError.startsWith("[")) {
            // Try to parse as JSON
            try {
              const cleanedError = errorData.error
                .replace(/'/g, '"')
                .replace(/ErrorDetail\(string=/g, '"')
                .replace(/, code=[^)]+\)/g, '"');

              const parsedError = JSON.parse(cleanedError);
              const firstKey = Object.keys(parsedError)[0];
              const firstMessage =
                parsedError[firstKey]?.[0] || "Validation error";
              toast.error(firstMessage);
              return;
            } catch (parseErr) {
              // If parsing fails, use the error string directly
              toast.error(errorData.error);
              return;
            }
          } else {
            // It's a simple string, use it directly
            toast.error(errorData.error);
            return;
          }
        }
        
        // Case 2: Check message field
        if (errorData?.message && typeof errorData.message === "string") {
          const messageParts = errorData.message.split(": ");
          if (messageParts.length > 1) {
            toast.error(messageParts[1]); // Just the message
          } else {
            toast.error(errorData.message);
          }
          return;
        }

        // Fallback
        toast.error("Failed to save user");
      }
    },
  });

  useEffect(() => {
    dispatch(fetchALLUserFormDropdowns());
  }, [dispatch]);

  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);

    // Auto-select India when creating a new user
    if (!user_id && countryList.length > 0) {
      const india = countryList.find(
        (c: any) => c.name === "India" || c.isoCode === "IN"
      );
      if (india) {
        setInitialValues((prev) => ({
          ...prev,
          country: india.name || "",
        }));
        // Load states for India
        const indiaStates = State.getStatesOfCountry(india.isoCode);
        setStates(indiaStates);
      }
    }
  }, [user_id]);

  // Auto-select last organization when in add mode
  useEffect(() => {
    if (!user_id && organizations.length > 0) {
      const lastOrg = organizations[organizations.length - 1];
      if (lastOrg) {
        formik.setFieldValue("organization", lastOrg.organization_id || "");
        formik.setFieldTouched("organization", true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, user_id]);

  useEffect(() => {
    if (
      formik.values.organization !== "" &&
      allDepartments &&
      allDepartments.length > 0
    ) {
      // Filter departments by organization_id from the all departments list
      const filteredDepartments = allDepartments.filter(
        (dept: any) => dept.organization_id === formik.values.organization
      );
      setDepartmentOptions(filteredDepartments);

      // Auto-select last department when in add mode
      if (!user_id && filteredDepartments.length > 0) {
        const lastDept = filteredDepartments[filteredDepartments.length - 1];
        if (lastDept) {
          formik.setFieldValue("department", lastDept.department_id || "");
          formik.setFieldTouched("department", true);
        }
      }
    } else {
      setDepartmentOptions([]);
      // Clear department when organization is cleared in add mode
      if (!user_id) {
        formik.setFieldValue("department", "");
        formik.setFieldTouched("department", false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.organization, allDepartments, user_id]);

  useEffect(() => {
    if (formik.values.department !== "" && teams && teams.length > 0) {
      // Filter teams by department_id
      const filteredTeams = teams.filter(
        (team: any) => team.department_id === formik.values.department
      );

      setTeamOptions(filteredTeams);

      // Auto-select last team when in add mode
      if (!user_id && filteredTeams.length > 0) {
        const lastTeam = filteredTeams[filteredTeams.length - 1];
        if (lastTeam) {
          formik.setFieldValue("team", lastTeam.team_id || "");
          formik.setFieldTouched("team", true);
        }
      }
    } else {
      setTeamOptions([]);
      // Clear team when department is cleared in add mode
      if (!user_id) {
        formik.setFieldValue("team", "");
        formik.setFieldTouched("team", false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.department, teams, user_id]);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  // After fetching user data, set countryId and formData
  useEffect(() => {
    // Reset ref if user_id changed
    if (user_id && lastFetchedUserId.current !== user_id) {
      isFormDataInitialized.current = false;
      lastFetchedUserId.current = user_id;
    }

    // Only fetch if we have a user_id and haven't already initialized for this user_id
    if (user_id && !isFormDataInitialized.current) {
      // Check if dropdowns are ready before fetching
      if (
        organizations.length === 0 ||
        userTypes.length === 0 ||
        allDepartments.length === 0
      ) {
        // Wait for dropdowns to be ready
        return;
      }

      isFormDataInitialized.current = true;
      setIsLoading(true);
      Axios.get(`/users/users/${user_id}/`)
        .then((res) => {
          const response = res?.data?.data;
          console.log("User data loaded:", response);

          const allCountries = Country.getAllCountries();
          const selectedCountry = allCountries.find(
            (c) => c.name === response.country
          );
          const countryIsoCode = selectedCountry?.isoCode || "";
          const statesOfCountry = State.getStatesOfCountry(countryIsoCode);
          const selectedState = statesOfCountry.find(
            (s) => s.name === response.state
          );
          const stateIsoCode = selectedState?.isoCode || "";

          const citiesOfState = City.getCitiesOfState(
            countryIsoCode,
            stateIsoCode
          );

          setCountries(allCountries);
          setStates(statesOfCountry);
          setCities(citiesOfState);

          // Extract IDs from nested objects
          const organizationId =
            response?.organization?.organization_id ||
            response?.organization ||
            "";
          const departmentId =
            response?.department?.department_id || response?.department || "";
          const teamId = response?.team?.team_id || response?.team || "";
          const userTypeId =
            response?.user_type?.user_type_id || response?.user_type || "";

          // Filter departments by organization_id from the all departments list
          if (organizationId && allDepartments && allDepartments.length > 0) {
            const filteredDepartments = allDepartments.filter(
              (dept: any) => dept.organization_id === organizationId
            );
            setDepartmentOptions(filteredDepartments);
          } else {
            setDepartmentOptions([]);
          }

          // Set team options based on the department (synchronously after departments are loaded)
          if (departmentId && teams && teams.length > 0) {
            const filteredTeams = teams.filter(
              (team: any) => team.department_id === departmentId
            );
            setTeamOptions(filteredTeams);
          } else {
            setTeamOptions([]);
          }

          // Set form data with all values including dropdown values (using extracted IDs)
          // This should happen after all dependent data is loaded
          setInitialValues({
            organization: organizationId,
            department: departmentId,
            team: teamId,
            first_name: response?.first_name || "",
            last_name: response?.last_name || "",
            username: response?.username || "",
            designation: response?.designation || "",
            user_type: userTypeId,
            email: response?.email || "",
            password: "",
            password2: "",
            mobile_number: response?.mobile_number || "",
            address: response?.address || "",
            city: response?.city || "",
            state: response?.state || "",
            country: response?.country || "",
            pincode: response?.pincode || "",
          });
        })
        .catch((error) => {
          console.log(error);
          isFormDataInitialized.current = false; // Reset on error to allow retry
        })
        .finally(() => setIsLoading(false));
    }

    // Reset the ref when user_id is cleared
    if (!user_id) {
      isFormDataInitialized.current = false;
      lastFetchedUserId.current = "";
    }
  }, [user_id, teams, userTypes, organizations, allDepartments]);

  const LoadingCard = () => (
    <div className="text-center py-5 bg-white rounded-3 shadow-sm">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  return (
    <div className="mt-3">
      {isLoading || (isEdit && user_id && !isFormDataInitialized.current) ? (
        <LoadingCard />
      ) : nextPage ? null : (
        <>
            <FormHeader
              title={`${user_id ? "Edit" : "Create"} User`}
              firstLabel="User"
              secondLabel={user_id ? "Edit User" : "Add User"}
              handleArrowClick={() => navigate("/user-list")}
            />
          {/* Card with form */}
          <Card>
            <Form noValidate onSubmit={formik.handleSubmit}>
              <Row className="mb-4" style={{ display: "none" }}>
                {/* Organisation */}
                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Organisation <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={organizations}
                    getOptionLabel={(option: any) =>
                      option.organization_name || ""
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    value={
                      organizations.find(
                        (org: any) =>
                          org.organization_id === formik.values.organization
                      ) || null
                    }
                    onChange={async (_, value) => {
                      const orgId = value?.organization_id || "";
                      await formik.setFieldValue("organization", orgId);

                      // Clear dependent fields
                      await formik.setFieldValue("department", "");
                      await formik.setFieldValue("team", "");

                      // Set touched states
                      await formik.setFieldTouched("organization", true);
                      await formik.setFieldTouched("department", false);
                      await formik.setFieldTouched("team", false);

                      // Validate only the organization field
                      formik.validateField("organization");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Organisation"
                        error={
                          !!(
                            formik.touched.organization &&
                            formik.errors.organization
                          )
                        }
                        helperText={
                          formik.touched.organization &&
                          formik.errors.organization ? (
                            <span>{formik.errors.organization}</span>
                          ) : null
                        }
                        fullWidth
                        size="small"
                        autoComplete="off"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                        InputProps={{
                          ...params.InputProps,
                          style: commonInputStyle,
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: commonLabelStyle,
                        }}
                      />
                    )}
                  />
                </Col>

                {/* Department */}
                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Department <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={departmentOptions}
                    getOptionLabel={(option: any) =>
                      option.department_name || ""
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    value={
                      departmentOptions?.find(
                        (dept: any) =>
                          dept?.department_id === formik.values.department
                      ) || null
                    }
                    onChange={async (_, value) => {
                      const deptId = value?.department_id || "";
                      await formik.setFieldValue("department", deptId);

                      // Clear dependent field
                      await formik.setFieldValue("team", "");

                      // Set touched states
                      await formik.setFieldTouched("department", true);
                      await formik.setFieldTouched("team", false);

                      // Validate only the department field
                      formik.validateField("department");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Department"
                        error={
                          !!(
                            formik.touched.department &&
                            formik.errors.department
                          )
                        }
                        helperText={
                          formik.touched.department &&
                          formik.errors.department ? (
                            <span>{formik.errors.department}</span>
                          ) : null
                        }
                        fullWidth
                        size="small"
                        autoComplete="off"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                        InputProps={{
                          ...params.InputProps,
                          style: commonInputStyle,
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: commonLabelStyle,
                        }}
                      />
                    )}
                  />
                </Col>

                {/* Team */}
                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Team <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={teamOptions}
                    getOptionLabel={(option: any) => option.team_name || ""}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    value={
                      teamOptions?.find(
                        (team: any) => team?.team_id === formik.values.team
                      ) || null
                    }
                    onChange={async (_, value) => {
                      const teamId = value?.team_id || "";
                      await formik.setFieldValue("team", teamId);

                      // Set touched state
                      await formik.setFieldTouched("team", true);

                      // Validate only the team field
                      formik.validateField("team");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Team"
                        error={!!(formik.touched.team && formik.errors.team)}
                        helperText={
                          formik.touched.team && formik.errors.team ? (
                            <span>{formik.errors.team}</span>
                          ) : null
                        }
                        fullWidth
                        size="small"
                        autoComplete="off"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                        InputProps={{
                          ...params.InputProps,
                          style: commonInputStyle,
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: commonLabelStyle,
                        }}
                      />
                    )}
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    First Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="first_name"
                    placeholder="Enter First Name"
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.touched.first_name && formik.errors.first_name)
                    }
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ color: "#dc3545" }}
                  >
                    {formik.touched.first_name && formik.errors.first_name}
                  </Form.Control.Feedback>
                </Col>

                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Last Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="last_name"
                    placeholder="Enter Last Name"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.touched.last_name && formik.errors.last_name)
                    }
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ color: "#dc3545" }}
                  >
                    {formik.touched.last_name && formik.errors.last_name}
                  </Form.Control.Feedback>
                </Col>

                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    User Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    placeholder="Enter User Name"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.touched.username && formik.errors.username)
                    }
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ color: "#dc3545" }}
                  >
                    {formik.touched.username && formik.errors.username}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Designation <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="designation"
                    placeholder="Enter Designation"
                    value={formik.values.designation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(
                        formik.touched.designation && formik.errors.designation
                      )
                    }
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ color: "#dc3545" }}
                  >
                    {formik.touched.designation && formik.errors.designation}
                  </Form.Control.Feedback>
                </Col>

                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    User Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={userTypes}
                    getOptionLabel={(option: any) => option.user_type || ""}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    value={
                      userTypes.find(
                        (ut: any) => ut.user_type_id === formik.values.user_type
                      ) || null
                    }
                    onChange={(_, value) => {
                      formik.setFieldValue(
                        "user_type",
                        value?.user_type_id || ""
                      );
                      formik.setFieldTouched("user_type", true);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select User Type"
                        error={
                          !!(
                            formik.touched.user_type && formik.errors.user_type
                          )
                        }
                        helperText={
                          formik.touched.user_type &&
                          formik.errors.user_type ? (
                            <span>{formik.errors.user_type}</span>
                          ) : null
                        }
                        fullWidth
                        size="small"
                        autoComplete="off"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                        InputProps={{
                          ...params.InputProps,
                          style: commonInputStyle,
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: commonLabelStyle,
                        }}
                      />
                    )}
                  />
                </Col>

                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!(formik.touched.email && formik.errors.email)}
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ color: "#dc3545" }}
                  >
                    {formik.touched.email && formik.errors.email}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-4">
                {user_id === "" && (
                  <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div style={{ position: "relative" }}>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Enter Password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!(formik.touched.password && formik.errors.password)
                        }
                        size="sm"
                        autoComplete="new-password"
                        style={{ ...commonInputStyle, paddingRight: 50 }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          zIndex: 10,
                          color: "#6c757d",
                          padding: "4px",
                        }}
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? (
                          <Visibility fontSize="small" />
                        ) : (
                          <VisibilityOff fontSize="small" />
                        )}
                      </span>
                    </div>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        minHeight: "1.25rem",
                        marginTop: "0.25rem",
                        color: "#dc3545",
                      }}
                    >
                      {formik.touched.password && formik.errors.password}
                    </Form.Control.Feedback>
                  </Col>
                )}

                {user_id === "" && (
                  <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Confirm Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div style={{ position: "relative" }}>
                      <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        name="password2"
                        placeholder="Enter Confirm Password"
                        value={formik.values.password2}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!(
                            formik.touched.password2 && formik.errors.password2
                          )
                        }
                        size="sm"
                        autoComplete="new-password"
                        style={{ ...commonInputStyle, paddingRight: 50 }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          zIndex: 10,
                          color: "#6c757d",
                          padding: "4px",
                        }}
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showConfirmPassword ? (
                          <Visibility fontSize="small" />
                        ) : (
                          <VisibilityOff fontSize="small" />
                        )}
                      </span>
                    </div>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        minHeight: "1.25rem",
                        marginTop: "0.25rem",
                        color: "#dc3545",
                      }}
                    >
                      {formik.touched.password2 && formik.errors.password2}
                    </Form.Control.Feedback>
                  </Col>
                )}

                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Mobile Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobile_number"
                    placeholder="Enter Mobile Number"
                    value={formik.values.mobile_number}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(
                        formik.touched.mobile_number &&
                        formik.errors.mobile_number
                      )
                    }
                    size="sm"
                    maxLength={10}
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={{
                      minHeight: "1.25rem",
                      marginTop: "0.25rem",
                      color: "#dc3545",
                    }}
                  >
                    {formik.touched.mobile_number &&
                      formik.errors.mobile_number}
                  </Form.Control.Feedback>
                </Col>
              </Row>
              <Row className="mb-4">
                <Col xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Address
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="address"
                    placeholder="Enter Address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.touched.address && formik.errors.address)
                    }
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ color: "#dc3545" }}
                  >
                    {formik.touched.address && formik.errors.address}
                  </Form.Control.Feedback>
                </Col>
              </Row>
              <Row className="mb-4">
                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Country
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    value={
                      countries.find(
                        (c: any) => c.name === formik.values.country
                      ) || null
                    }
                    onChange={(e, selectedCountry) => {
                      formik.setFieldValue(
                        "country",
                        selectedCountry?.name || ""
                      );
                      formik.setFieldValue("state", "");
                      formik.setFieldValue("city", "");
                      formik.setFieldTouched("country", true);

                      if (selectedCountry) {
                        setIsLoadingStates(true);
                        const newStates = State.getStatesOfCountry(
                          selectedCountry.isoCode
                        );
                        setStates(newStates);
                        setCities([]);
                        setIsLoadingStates(false);
                      }
                    }}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select Country"
                        size="small"
                        error={
                          !!(formik.touched.country && formik.errors.country)
                        }
                        helperText={
                          formik.touched.country && formik.errors.country ? (
                            <span
                              style={{ color: "#dc3545", fontSize: "0.875rem" }}
                            >
                              {formik.errors.country}
                            </span>
                          ) : null
                        }
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                  />
                </Col>

                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    State
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={states}
                    getOptionLabel={(option) => option.name}
                    loading={isLoadingStates}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    value={
                      states.find((s: any) => s.name === formik.values.state) ||
                      null
                    }
                    onChange={(e, selectedState) => {
                      const selectedCountry = countries.find(
                        (c: any) => c.name === formik.values.country
                      );

                      formik.setFieldValue("state", selectedState?.name || "");
                      formik.setFieldValue("city", "");
                      formik.setFieldTouched("state", true);

                      if (selectedCountry && selectedState) {
                        setIsLoadingCities(true);
                        const newCities = City.getCitiesOfState(
                          selectedCountry.isoCode,
                          selectedState.isoCode
                        );
                        setCities(newCities);
                        setIsLoadingCities(false);
                      }
                    }}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select State"
                        size="small"
                        error={!!(formik.touched.state && formik.errors.state)}
                        helperText={
                          formik.touched.state && formik.errors.state ? (
                            <span
                              style={{ color: "#dc3545", fontSize: "0.875rem" }}
                            >
                              {formik.errors.state}
                            </span>
                          ) : null
                        }
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                    disabled={!formik.values.country}
                  />
                </Col>

                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    City
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={cities}
                    getOptionLabel={(option) => option.name}
                    loading={isLoadingCities}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    value={
                      cities.find((c: any) => c.name === formik.values.city) ||
                      null
                    }
                    onChange={(e, selectedCity) => {
                      formik.setFieldValue("city", selectedCity?.name || "");
                      formik.setFieldTouched("city", true);
                    }}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select City"
                        size="small"
                        error={!!(formik.touched.city && formik.errors.city)}
                        helperText={
                          formik.touched.city && formik.errors.city ? (
                            <span
                              style={{ color: "#dc3545", fontSize: "0.875rem" }}
                            >
                              {formik.errors.city}
                            </span>
                          ) : null
                        }
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                    disabled={!formik.values.state}
                  />
                </Col>
              </Row>
              <Row className="mb-4">
                <Col lg={4} md={4} sm={6} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Pincode
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="pincode"
                    placeholder="Enter Pincode"
                    value={formik.values.pincode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.touched.pincode && formik.errors.pincode)
                    }
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ color: "#dc3545" }}
                  >
                    {formik.touched.pincode && formik.errors.pincode}
                  </Form.Control.Feedback>
                </Col>
              </Row>
              <div className="d-flex justify-content-center gap-3 mt-4 mb-3">
                <CancelBackButton to="/user-list" disabled={formik.isSubmitting} />
                <SubmitButton
                  isSubmitting={formik.isSubmitting}
                  isEdit={isEdit}
                  labels={{
                    create: "Create User",
                    update: "Update",
                    submitting: "Creating...",
                    updating: "Updating...",
                  }}
                />
              </div>
            </Form>
          </Card>
        </>
      )}
    </div>
  );
};

export default BasicDetails;
